import { Component, ElementRef, HostListener, OnInit, Renderer2, ViewChild } from "@angular/core";
import { RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { NgClass, NgIf, NgOptimizedImage, TitleCasePipe, UpperCasePipe } from "@angular/common";
import { CdkMenu, CdkMenuItem } from "@angular/cdk/menu";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faAddressCard,
  faBell,
  faBoxArchive,
  faBuilding,
  faCartShoppingFast,
  faCommentSms,
  faCreditCard,
  faEnvelope,
  faFilter,
  faFlag,
  faHashtag,
  faHouseBlank,
  faLinkSimple,
  faPhoneVolume,
  faShop,
  faSignalStream,
  faStar,
  faTableList,
  faUserCheck,
  faUsers,
  faUserTag,
  faSquareInfo,
  faMessages,
  faSparkles,
  faRightFromBracket,
  faPaperPlane
} from "@fortawesome/pro-light-svg-icons";
import { KeycloakAngularModule } from "keycloak-angular";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { faCircleUser, faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { AuthGuard } from "../../auth/auth.guard";
import { UserService } from "../../services/user.service";
import { User } from "../../models/user.model";
import { EMPTY, mergeMap, Observable } from "rxjs";
import { DropdownComponent } from "../dropdown/dropdown.component";
import { Option } from "../../models/option.model";
import { DropdownType } from "../../models/dropdown-type.enum";
import { ConnectedPosition } from "@angular/cdk/overlay";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { StateManagementService } from "../../services/state-management.service";
import { Environment } from "../../../environments/environment.model";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-admin",
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    CdkMenu,
    CdkMenuItem,
    FontAwesomeModule,
    KeycloakAngularModule,
    NgIf,
    NgClass,
    DropdownComponent,
    TitleCasePipe,
    UpperCasePipe
  ],
  templateUrl: "./admin.component.html",
  styleUrl: "./admin.component.scss"
})
export class AdminComponent implements OnInit {
  protected readonly faFilter: IconDefinition = faFilter;
  protected readonly faAddressCard: IconDefinition = faAddressCard;
  protected readonly faCreditCard: IconDefinition = faCreditCard;
  protected readonly faBell: IconDefinition = faBell;
  protected readonly faUserCheck: IconDefinition = faUserCheck;
  protected readonly faCartShoppingFast: IconDefinition = faCartShoppingFast;
  protected readonly faCommentSms: IconDefinition = faCommentSms;
  protected readonly faHouseBlank: IconDefinition = faHouseBlank;
  protected readonly faTableList: IconDefinition = faTableList;
  protected readonly faUsers: IconDefinition = faUsers;
  protected readonly faStar: IconDefinition = faStar;
  protected readonly faLinkSimple: IconDefinition = faLinkSimple;
  protected readonly faHashtag: IconDefinition = faHashtag;
  protected readonly faUserTag: IconDefinition = faUserTag;
  protected readonly faPhoneVolume: IconDefinition = faPhoneVolume;
  protected readonly faBuilding: IconDefinition = faBuilding;
  protected readonly faChevronLeft: IconDefinition = faChevronLeft;
  protected readonly faSignalStream: IconDefinition = faSignalStream;
  protected readonly faBoxArchive: IconDefinition = faBoxArchive;
  protected readonly faShop: IconDefinition = faShop;
  protected readonly faEnvelope: IconDefinition = faEnvelope;
  protected readonly faFlag: IconDefinition = faFlag;
  protected readonly faSquareInfo: IconDefinition = faSquareInfo;
  protected readonly faMessages: IconDefinition = faMessages;
  protected readonly faSparkles: IconDefinition = faSparkles;
  protected readonly faRightFromBracket: IconDefinition = faRightFromBracket;
  protected readonly faPaperPlane: IconDefinition = faPaperPlane;
  protected readonly faChevronDown: IconDefinition = faChevronDown;
  protected readonly faChevronUp: IconDefinition = faChevronUp;

  protected readonly DropdownType: typeof DropdownType = DropdownType;
  protected profileDropdownPosition: ConnectedPosition = {
    originX: "start",
    originY: "bottom",
    overlayX: "end",
    overlayY: "top"
  };
  protected user: User | null = null;
  protected profileMenuOptions: Option[] = [{ field: "logout", displayName: "Logout" }];
  protected readonly environment: Environment = environment;

  constructor(
    protected authGuard: AuthGuard,
    protected userService: UserService,
    protected readonly stateManagementService: StateManagementService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.authGuard
      .fetchUsername()
      .pipe(
        mergeMap((username: string | null): Observable<User> => {
          if (username != null) {
            return this.userService.getUser(username);
          }
          return EMPTY;
        })
      )
      .subscribe((user: User): User => (this.user = user));

    this.addInlineScript();
    this.loadScripts();
  }

  handleProfileMenuOptionSelected(option: Option): void {
    if (option.field === "logout") {
      this.authGuard.logout();
    }
  }

  loadScripts(): void {
    const scripts = [
      { src: "//cs.iubenda.com/sync/2741231.js" },
      { src: "//cdn.iubenda.com/cs/gpp/stub.js" },
      { src: "//cdn.iubenda.com/cs/iubenda_cs.js", charset: "UTF-8", async: true }
    ];

    scripts.forEach((scriptInfo): void => {
      const script = this.renderer.createElement("script");
      script.src = scriptInfo.src;
      script.type = "text/javascript";
      script.async = true;

      if (scriptInfo.charset) {
        script.charset = scriptInfo.charset;
      }

      this.renderer.appendChild(document.head, script);
    });
  }

  addInlineScript(): void {
    const scriptContent = `
      var _iub = _iub || [];
        _iub.csConfiguration = {
          countryDetection: true,
          enableGdpr: false,
          enableUspr: true,
          lang: "en",
          siteId: 2741231,
          tcfPurposes: {
            2: "consent_only",
            7: "consent_only",
            8: "consent_only",
            9: "consent_only",
            10: "consent_only",
          },
          usprPurposes: "s,sh,adv",
          cookiePolicyId: 75803826,
          banner: {
            backgroundColor: "#FFFFFF",
            closeButtonDisplay: false,
            position: "float-bottom-left",
            textColor: "#000000",
          },
        };
    `;

    const script: any = this.renderer.createElement("script");
    script.type = "text/javascript";
    script.text = scriptContent;
    this.renderer.appendChild(document.head, script);
  }

  currentYear(): number {
    return new Date().getFullYear();
  }

  protected readonly faCircleUser = faCircleUser;

  isUserProfileDropdownOpen = false;

  @ViewChild("dropdownWrapper", { static: true }) dropdownWrapper!: ElementRef;

  toggleDropdown(): void {
    this.isUserProfileDropdownOpen = !this.isUserProfileDropdownOpen;
  }

  @HostListener("document:click", ["$event"])
  handleClickOutside(event: MouseEvent): void {
    if (!this.dropdownWrapper.nativeElement.contains(event.target)) {
      this.isUserProfileDropdownOpen = false;
    }
  }
}
